import React, { useState } from 'react';
import { 
  ShieldCheck, 
  Award, 
  Cpu, 
  FileCheck,
  Landmark,
  Building2,
  Home as HomeIcon,
  Lightbulb,
  Code,
  Lock,
  Cloud,
  BarChart,
  Shield,
  ArrowRight,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { stats, whyChooseUs, governmentSectors, services, handleContactSubmit } from '../mock';

const iconMap = {
  'shield-check': ShieldCheck,
  'award': Award,
  'cpu': Cpu,
  'file-check': FileCheck,
  'landmark': Landmark,
  'building-2': Building2,
  'home': HomeIcon,
  'lightbulb': Lightbulb,
  'code': Code,
  'lock': Lock,
  'cloud': Cloud,
  'bar-chart': BarChart,
  'shield': Shield
};

const Home = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const result = await handleContactSubmit(formData);
      if (result.success) {
        toast({
          title: "Success!",
          description: result.message,
        });
        setFormData({ name: '', email: '', organization: '', message: '' });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-red-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_artemis-gov/artifacts/405ugilo_IMG_0035.png" 
                alt="Artemis Red Logo" 
                className="h-16 w-auto"
              />
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-gray-300 hover:text-red-600 transition-colors duration-300">Services</a>
              <a href="#sectors" className="text-gray-300 hover:text-red-600 transition-colors duration-300">Sectors</a>
              <a href="#why-us" className="text-gray-300 hover:text-red-600 transition-colors duration-300">Why Us</a>
              <a href="#contact" className="text-gray-300 hover:text-red-600 transition-colors duration-300">Contact</a>
              <Button className="bg-red-600 hover:bg-red-700 text-white transition-all duration-300">
                Get Started
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/25819424/pexels-photo-25819424.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Government Building"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              Specialized Technology for
              <span className="block mt-2 bg-gradient-to-r from-red-600 via-yellow-600 to-red-600 bg-clip-text text-transparent">
                Government Excellence
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-10 leading-relaxed">
              Delivering secure, compliant, and innovative technology solutions to federal, state, and local government agencies.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg" 
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
                asChild
              >
                <a href="mailto:consult@artemisred.com?subject=Schedule%20Consultation">
                  Schedule Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-yellow-600 text-yellow-600 hover:bg-yellow-600 hover:text-black px-8 py-6 text-lg transition-all duration-300"
              >
                Our Capabilities
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-b from-red-950/20 to-black border-y border-red-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={stat.id} 
                className="text-center transform hover:scale-110 transition-all duration-300"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="text-4xl md:text-5xl font-bold text-red-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-sm md:text-base text-gray-400">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why Choose <span className="text-red-600">Artemis Red</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Your trusted partner in government technology transformation
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <Card 
                  key={item.id} 
                  className="bg-gradient-to-br from-red-950/30 to-black border border-red-900/30 hover:border-red-600/50 transition-all duration-500 hover:scale-105 group"
                >
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="p-3 bg-red-600/10 rounded-lg group-hover:bg-red-600 transition-colors duration-300">
                          <Icon className="h-8 w-8 text-red-600 group-hover:text-white transition-colors duration-300" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-3 group-hover:text-red-600 transition-colors duration-300">
                          {item.title}
                        </h3>
                        <p className="text-gray-400 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Government Sectors Section */}
      <section id="sectors" className="py-24 bg-gradient-to-b from-black to-red-950/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Government Sectors <span className="text-yellow-600">We Serve</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Comprehensive solutions across all levels of government
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {governmentSectors.map((sector) => {
              const Icon = iconMap[sector.icon];
              return (
                <Card 
                  key={sector.id} 
                  className="bg-black border-2 border-yellow-600/20 hover:border-yellow-600 transition-all duration-500 hover:scale-105 group"
                >
                  <CardContent className="p-8 text-center">
                    <div className="flex justify-center mb-6">
                      <div className="p-4 bg-yellow-600/10 rounded-full group-hover:bg-yellow-600 transition-all duration-300">
                        <Icon className="h-12 w-12 text-yellow-600 group-hover:text-black transition-colors duration-300" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-yellow-600 transition-colors duration-300">
                      {sector.name}
                    </h3>
                    <p className="text-gray-400 leading-relaxed">
                      {sector.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our <span className="text-red-600">Services</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              End-to-end technology solutions designed for government excellence
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card 
                  key={service.id} 
                  className="bg-gradient-to-br from-red-950/20 to-black border border-red-900/30 hover:border-red-600 transition-all duration-500 hover:scale-105 group"
                >
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <div className="inline-block p-3 bg-red-600/10 rounded-lg group-hover:bg-red-600 transition-colors duration-300">
                        <Icon className="h-8 w-8 text-red-600 group-hover:text-white transition-colors duration-300" />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-red-600 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-b from-black to-red-950/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Let's Discuss Your <span className="text-red-600">Project</span>
              </h2>
              <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                Ready to modernize your government technology infrastructure? Contact us today for a consultation.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-red-600/10 rounded-lg">
                    <Mail className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Email</div>
                    <a href="mailto:consult@artemisred.com" className="text-white hover:text-red-600 transition-colors duration-300">
                      consult@artemisred.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-red-600/10 rounded-lg">
                    <Phone className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Phone</div>
                    <a href="tel:+14043886205" className="text-white hover:text-red-600 transition-colors duration-300">
                      +1 (404) 388-6205
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-red-600/10 rounded-lg">
                    <MapPin className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Address</div>
                    <div className="text-white">Atlanta, Georgia</div>
                  </div>
                </div>
              </div>
            </div>

            <Card className="bg-gradient-to-br from-red-950/30 to-black border border-red-900/30">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="bg-black/50 border-red-900/30 text-white focus:border-red-600 transition-colors duration-300"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="bg-black/50 border-red-900/30 text-white focus:border-red-600 transition-colors duration-300"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="organization" className="block text-sm font-medium text-gray-300 mb-2">
                      Organization
                    </label>
                    <Input
                      id="organization"
                      name="organization"
                      type="text"
                      value={formData.organization}
                      onChange={handleInputChange}
                      className="bg-black/50 border-red-900/30 text-white focus:border-red-600 transition-colors duration-300"
                      placeholder="Your Agency/Department"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                      className="bg-black/50 border-red-900/30 text-white focus:border-red-600 transition-colors duration-300"
                      placeholder="Tell us about your project requirements..."
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg transition-all duration-300 hover:scale-105"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-red-900/20 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_artemis-gov/artifacts/405ugilo_IMG_0035.png" 
                  alt="Artemis Red Logo" 
                  className="h-16 w-auto"
                />
              </div>
              <p className="text-gray-400 mb-4">
                Specialized technology solutions for government excellence.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#services" className="text-gray-400 hover:text-red-600 transition-colors duration-300">Services</a></li>
                <li><a href="#sectors" className="text-gray-400 hover:text-red-600 transition-colors duration-300">Sectors</a></li>
                <li><a href="#why-us" className="text-gray-400 hover:text-red-600 transition-colors duration-300">Why Us</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-red-600 transition-colors duration-300">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-red-600 transition-colors duration-300">Privacy Policy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-red-600 transition-colors duration-300">Terms of Service</a></li>
                <li><a href="#" className="text-gray-400 hover:text-red-600 transition-colors duration-300">Security</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-red-900/20 pt-8 text-center">
            <p className="text-gray-500">
              © {new Date().getFullYear()} Artemis Red LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
